import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fmt } from '../api/services.api';
import { useCartStore } from '../../cart/store/cartStore';
import { useToast } from '../../../shared/components/Toast/ToastProvider';
import { BEAUTY_BOOKING } from '../data/customFlows';

const PRIMARY = '#082189';

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const Head = styled.div`
  padding: 20px 20px 14px;
  border-bottom: 1px solid #f1f2f4;

  @media (max-width: 480px) {
    padding: 16px 16px 12px;
  }
`;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 900;
  color: #0f0f0f;
  letter-spacing: -0.4px;
  line-height: 1.25;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #6b7280;
  b { color: #0f0f0f; }
`;

const Body = styled.div`
  padding: 16px 20px 18px;

  @media (max-width: 480px) {
    padding: 14px 16px 16px;
  }
`;

const CardsRow = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 4px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const VCard = styled.div`
  flex: 0 0 165px;
  scroll-snap-align: start;
  border: 1px solid #ececec;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  transition: border-color 0.18s, box-shadow 0.18s;
  ${p => p.$active && `border-color: ${PRIMARY}; box-shadow: 0 0 0 2px ${PRIMARY}22;`}
`;

const VImgWrap = styled.div`
  position: relative;
  height: 122px;
  overflow: hidden;
  background: #f6f6f6;
`;

const VImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Badge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #3d0e2f;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 6px;
`;

const VBody = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const VName = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0f0f0f;
  line-height: 1.3;
`;

const VRating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  b { color: #f59e0b; }
`;

const VPrice = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0f0f0f;
  margin-top: 2px;
`;

const AddBtn = styled.button`
  margin-top: 8px;
  width: 100%;
  border: 1.5px solid ${PRIMARY};
  background: #ffffff;
  color: ${PRIMARY};
  font-size: 13px;
  font-weight: 800;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${PRIMARY}0d; }
`;

const Stepper = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1.5px solid ${PRIMARY};
  border-radius: 8px;
  padding: 4px 12px;
`;

const StepBtn = styled.button`
  border: none;
  background: none;
  color: ${PRIMARY};
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
`;

const StepQty = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #0f0f0f;
`;

const LuxeStrip = styled.div`
  margin-top: 18px;
  background: #f3f2ef;
  border-radius: 14px;
  padding: 18px 18px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LuxeTitle = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: #0f0f0f;
  letter-spacing: -0.4px;
  margin-bottom: 2px;
`;

const LuxeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const FooterRow = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f1f2f4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`;

const TotalCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const TotalLabel = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
`;

const TotalValue = styled.span`
  font-size: 20px;
  font-weight: 900;
  color: #0f0f0f;
`;

const CtaBtn = styled.button`
  border: none;
  cursor: pointer;
  background: ${p => (p.$disabled ? '#c7cbdb' : PRIMARY)};
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  padding: 12px 30px;
  border-radius: 10px;
  pointer-events: ${p => (p.$disabled ? 'none' : 'auto')};
  transition: opacity 0.15s, transform 0.12s;
  &:hover { opacity: 0.92; transform: scale(1.02); }

  @media (max-width: 480px) {
    flex: 1 1 auto;
    min-width: 140px;
    padding: 12px 20px;
  }
`;

// Inline (non-modal) beauty variant selector — horizontal product cards with
// quantity steppers, shown directly on the Glam & Glow service detail page.
const BeautyVariantSection = ({ serviceName, ratingLabel, variants: variantsProp, ctx = {}, destination = '/checkout' }) => {
  const navigate = useNavigate();
  const addItem = useCartStore(s => s.addItem);
  const toast = useToast();

  const variants = Array.isArray(variantsProp) && variantsProp.length ? variantsProp : BEAUTY_BOOKING.variants;
  const [qty, setQty] = useState({});

  const setCount = (id, n) => setQty(prev => {
    const next = { ...prev };
    if (n <= 0) delete next[id];
    else next[id] = n;
    return next;
  });

  const total = useMemo(
    () => variants.reduce((sum, v) => sum + v.price * (qty[v.id] || 0), 0),
    [qty, variants],
  );
  const count = Object.values(qty).reduce((a, b) => a + b, 0);

  const handleDone = () => {
    if (count === 0) return;
    variants.forEach(v => {
      const c = qty[v.id] || 0;
      if (c <= 0) return;
      addItem({
        subServiceID: `beauty-${v.id}`,
        subServiceName: `${serviceName} · ${v.name}`,
        quantity: c,
        price: v.price,
        cgst: 0,
        sgst: 0,
        imageURL: v.image,
        serviceID: ctx.serviceID,
        subCategoryID: ctx.subCategoryID,
        categoryName: ctx.categoryName || 'Glam & Glow',
        categorySlug: ctx.categorySlug || 'glam-glow',
        packageID: 1,
      });
    });
    toast.success('Added to cart');
    navigate(destination);
  };

  return (
    <Section>
      <Head>
        <Title>{serviceName || 'Choose your wax'}</Title>
        <RatingRow><b>★</b> {ratingLabel || BEAUTY_BOOKING.ratingLabel}</RatingRow>
      </Head>
      <Body>
        <CardsRow>
          {variants.map(v => {
            const c = qty[v.id] || 0;
            return (
              <VCard key={v.id} $active={c > 0}>
                <VImgWrap>
                  <VImg src={v.image} alt={v.name} onError={e => { e.target.style.opacity = 0; }} />
                  {v.badge ? <Badge>{v.badge}</Badge> : null}
                </VImgWrap>
                <VBody>
                  <VName>{v.name}</VName>
                  <VRating><b>★</b> {v.rating} ({v.reviews} reviews)</VRating>
                  <VPrice>{fmt(v.price)}</VPrice>
                  {c > 0 ? (
                    <Stepper>
                      <StepBtn onClick={() => setCount(v.id, c - 1)}>−</StepBtn>
                      <StepQty>{c}</StepQty>
                      <StepBtn onClick={() => setCount(v.id, c + 1)}>+</StepBtn>
                    </Stepper>
                  ) : (
                    <AddBtn onClick={() => setCount(v.id, 1)}>Add</AddBtn>
                  )}
                </VBody>
              </VCard>
            );
          })}
        </CardsRow>

        {/* <LuxeStrip>
          <LuxeTitle>LUXE difference</LuxeTitle>
          <LuxeItem>👩‍🔬 Top-rated experts</LuxeItem>
          <LuxeItem>✨ Premium brands</LuxeItem>
          <LuxeItem>👍 Elevated experience</LuxeItem>
        </LuxeStrip> */}

        <FooterRow>
          <TotalCol>
            <TotalLabel>{count > 0 ? `${count} item${count > 1 ? 's' : ''}` : 'Total'}</TotalLabel>
            <TotalValue>{fmt(total)}</TotalValue>
          </TotalCol>
          <CtaBtn $disabled={count === 0} onClick={handleDone}>Done</CtaBtn>
        </FooterRow>
      </Body>
    </Section>
  );
};

export default BeautyVariantSection;
